import React, { useRef, useState } from 'react';
import { uploadImage } from '@/lib/upload';
import { Button } from '@/components/ui/button';
import { Upload, CheckCircle2, X, Image as ImageIcon } from 'lucide-react';

/**
 * Reusable screenshot/photo upload widget.
 * - Shows clear "uploading…" / "uploaded ✓" status
 * - Prevents form-submit until upload completes (via onStatusChange)
 * - Shows live preview thumbnail
 * - One-click "remove" to re-upload
 *
 * Props:
 *   value: current URL (string|null)
 *   onChange(url): called with the new URL after successful upload (or null on remove)
 *   onStatusChange(busy: bool): called whenever upload starts/ends — parent can disable Submit
 *   label: optional label text shown above
 *   accept: file accept attribute (default image/*)
 *   testId: data-testid suffix
 */
export const UploadField = ({ value, onChange, onStatusChange, label, accept = 'image/*', testId = 'upload' }) => {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const handle = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true); onStatusChange?.(true);
    const res = await uploadImage(file);
    setBusy(false); onStatusChange?.(false);
    if (res) onChange?.(res.url);
    e.target.value = '';
  };

  return (
    <div className="space-y-2">
      {label && <div className="text-sm font-medium">{label}</div>}
      {!value && (
        <Button
          type="button"
          variant="outline"
          onClick={() => inputRef.current?.click()}
          disabled={busy}
          className="w-full gap-2 h-24 border-dashed"
          data-testid={`${testId}-pick`}
        >
          {busy ? <><Upload className="h-4 w-4 animate-pulse" /> Uploading… please wait</> : <><ImageIcon className="h-4 w-4" /> Click to upload (will compress)</>}
        </Button>
      )}
      {value && (
        <div className="relative inline-block">
          <img src={value} alt="" className="h-28 w-auto rounded-md border" data-testid={`${testId}-preview`} />
          <div className="absolute -top-2 -right-2 flex gap-1">
            <button
              type="button"
              onClick={() => onChange?.(null)}
              className="bg-red-500 text-white rounded-full h-6 w-6 grid place-items-center text-xs"
              data-testid={`${testId}-remove`}
              aria-label="Remove"
            >
              <X className="h-3 w-3" />
            </button>
          </div>
          <div className="absolute bottom-1 left-1 bg-[#1D4ED8] text-white text-[10px] px-1.5 py-0.5 rounded-full flex items-center gap-1">
            <CheckCircle2 className="h-3 w-3" /> Uploaded
          </div>
        </div>
      )}
      <input ref={inputRef} type="file" accept={accept} hidden onChange={handle} data-testid={`${testId}-input`} />
    </div>
  );
};

export default UploadField;
